/**
 * Freight density, NMFC class, cube and linear-foot calculations.
 *
 * Pure and dependency-free so the API, the rate-shop orchestrator and the web quote wizard
 * all compute identical numbers. Dimensions are inches, weights pounds, density pounds per
 * cubic foot (pcf).
 *
 * Density-based class is the default for most commodities under the NMFC. Some NMFC items are
 * classed by commodity rather than density; when a shipper declares a class or NMFC item we keep
 * it and only flag a mismatch, never silently override it.
 */
import type { Dimensions, FreightClass, HandlingUnit } from "./domain.js";
import { FREIGHT_CLASSES, LIMITS } from "./domain.js";

export class FreightCalcError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FreightCalcError";
  }
}

/** One density bracket: densities `>= minPcf` (and below the next bracket) take `freightClass`. */
export interface DensityBracket {
  readonly minPcf: number;
  readonly freightClass: FreightClass;
}

/**
 * NMFC 13-sub-provision density scale (Docket 2025-1, effective 2025-07-19).
 * Ordered by descending `minPcf`; lower bounds are inclusive ("1 but less than 2").
 */
export const NMFC_DENSITY_SCALE_2025: readonly DensityBracket[] = [
  { minPcf: 50, freightClass: "50" },
  { minPcf: 35, freightClass: "55" },
  { minPcf: 30, freightClass: "60" },
  { minPcf: 22.5, freightClass: "65" },
  { minPcf: 15, freightClass: "70" },
  { minPcf: 12, freightClass: "85" },
  { minPcf: 10, freightClass: "92.5" },
  { minPcf: 8, freightClass: "100" },
  { minPcf: 6, freightClass: "125" },
  { minPcf: 4, freightClass: "175" },
  { minPcf: 2, freightClass: "250" },
  { minPcf: 1, freightClass: "300" },
  { minPcf: 0, freightClass: "400" },
];

/** Pre-2025 11-sub-provision scale (item 100 series), kept for carriers/tariffs still on it. */
export const NMFC_DENSITY_SCALE_LEGACY: readonly DensityBracket[] = [
  { minPcf: 30, freightClass: "60" },
  { minPcf: 22.5, freightClass: "65" },
  { minPcf: 15, freightClass: "70" },
  { minPcf: 12, freightClass: "85" },
  { minPcf: 10, freightClass: "92.5" },
  { minPcf: 8, freightClass: "100" },
  { minPcf: 6, freightClass: "125" },
  { minPcf: 4, freightClass: "175" },
  { minPcf: 2, freightClass: "250" },
  { minPcf: 1, freightClass: "300" },
  { minPcf: 0, freightClass: "400" },
];

export type DensityScaleName = "nmfc2025" | "legacy";

export const DENSITY_SCALES: Readonly<Record<DensityScaleName, readonly DensityBracket[]>> = {
  nmfc2025: NMFC_DENSITY_SCALE_2025,
  legacy: NMFC_DENSITY_SCALE_LEGACY,
};

const CUBIC_INCHES_PER_CUBIC_FOOT = 1728;

function assertPositive(n: unknown, label: string): void {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) {
    throw new FreightCalcError(`${label} must be a positive finite number`);
  }
}

const NMFC_CLASS_SET: ReadonlySet<string> = new Set(FREIGHT_CLASSES);

/** True only for an own-string NMFC class ("77.5"), never a coerced number or prototype key. */
function isNmfcClass(c: unknown): c is FreightClass {
  return typeof c === "string" && NMFC_CLASS_SET.has(c);
}

function assertObject(v: unknown, label: string): void {
  if (typeof v !== "object" || v === null || Array.isArray(v)) throw new FreightCalcError(`${label} must be an object`);
}

/** Hostile-input guard for one handling unit: shape only; numeric checks happen where used. */
function assertUnitShape(u: unknown, label = "handling unit"): void {
  assertObject(u, label);
  assertObject((u as { dims?: unknown }).dims, `${label} dims`);
}

/** Bracket list for a scale name or explicit list; unknown/prototype names are a domain error. */
function resolveScale(scale: unknown): readonly DensityBracket[] {
  if (typeof scale === "string") {
    if (!Object.prototype.hasOwnProperty.call(DENSITY_SCALES, scale)) throw new FreightCalcError(`unknown density scale "${scale.slice(0, 32)}"`);
    return DENSITY_SCALES[scale as DensityScaleName];
  }
  if (!Array.isArray(scale)) throw new FreightCalcError("density scale must be a scale name or bracket list");
  return scale as readonly DensityBracket[];
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round((n + Number.EPSILON) * f) / f;
}

/** Cubic feet of one piece with the given dimensions (inches). */
export function cubicFeet(dims: Dimensions): number {
  assertObject(dims, "dims");
  assertPositive(dims.lengthIn, "lengthIn");
  assertPositive(dims.widthIn, "widthIn");
  assertPositive(dims.heightIn, "heightIn");
  const cube = (dims.lengthIn * dims.widthIn * dims.heightIn) / CUBIC_INCHES_PER_CUBIC_FOOT;
  // Huge dims overflow to Infinity and tiny ones underflow to 0: fail here, not as NaN downstream.
  if (!Number.isFinite(cube) || cube <= 0) throw new FreightCalcError("dimensions are out of range");
  return cube;
}

/** Total cubic feet of a handling-unit line (per-unit dims x quantity). */
export function lineCubicFeet(u: Pick<HandlingUnit, "dims" | "quantity">): number {
  assertUnitShape(u);
  if (!Number.isSafeInteger(u.quantity) || u.quantity < 1) throw new FreightCalcError("quantity must be a positive integer");
  const total = cubicFeet(u.dims) * u.quantity;
  if (!Number.isFinite(total)) throw new FreightCalcError("line cube is out of range");
  return total;
}

/** Density in pounds per cubic foot. */
export function densityPcf(weightLb: number, cubicFt: number): number {
  assertPositive(weightLb, "weightLb");
  assertPositive(cubicFt, "cubicFt");
  const d = weightLb / cubicFt;
  if (!Number.isFinite(d) || d <= 0) throw new FreightCalcError("density is out of range");
  return d;
}

/**
 * Density truncated to hundredths, the precision it is shown and classed at. Truncation, not
 * rounding: NMFC brackets are "15 but less than 22.5", so 14.996 pcf is class 85, and rounding
 * half-up to 15.00 would under-class it (reclass risk). A 1e-6 tolerance absorbs float noise
 * (14.999999999 from 0.1-style arithmetic counts as 15).
 */
export function densityHundredths(pcf: number): number {
  return Math.floor(pcf * 100 + 1e-6) / 100;
}

/**
 * NMFC class for a density. Density is truncated to 2 decimals first (densityHundredths) so the
 * class always matches the displayed pcf and float noise (14.999999999) can't drop a value below
 * a bracket boundary.
 */
export function classForDensity(pcf: number, scale: DensityScaleName | readonly DensityBracket[] = "nmfc2025"): FreightClass {
  if (typeof pcf !== "number" || !Number.isFinite(pcf) || pcf < 0) {
    throw new FreightCalcError("density must be a non-negative finite number");
  }
  const brackets = resolveScale(scale);
  const d = densityHundredths(pcf);
  for (const b of brackets) {
    if (typeof b !== "object" || b === null) throw new FreightCalcError("density scale has a malformed bracket");
    if (d >= b.minPcf) return checkedClass(b.freightClass);
  }
  const last = brackets[brackets.length - 1];
  if (!last) throw new FreightCalcError("density scale is empty");
  return checkedClass(last.freightClass);
}

function checkedClass(c: unknown): FreightClass {
  if (!isNmfcClass(c)) throw new FreightCalcError("density scale maps to a class outside the NMFC set");
  return c;
}

/** Trailer interior used for linear-foot estimates. Defaults to a 53' dry van. */
export interface TrailerSpec {
  lengthIn: number;
  widthIn: number;
  heightIn: number;
}

export const TRAILER_53FT: TrailerSpec = {
  lengthIn: LIMITS.maxLengthIn,
  widthIn: LIMITS.maxWidthIn,
  heightIn: LIMITS.maxHeightIn,
};

export interface LinearFootLine {
  /** Floor positions after stacking. */
  positions: number;
  /** Units stacked per floor position (1 when not stackable or too tall). */
  stackHeight: number;
  /** Positions side by side across the trailer width. */
  perRow: number;
  /** Trailer length consumed by this line, inches. */
  linearIn: number;
  /** True when a single piece is longer than the trailer or wider than its door width. */
  overDimension: boolean;
}

/**
 * Trailer floor length one line consumes. Heuristic used by LTL carriers for linear-foot
 * rules: stack when the unit is stackable and stacking fits under the roof, place two across
 * when the narrow side fits half the width (narrow side across), otherwise one across.
 */
export function lineLinearFeet(u: Pick<HandlingUnit, "dims" | "quantity" | "stackable">, trailer: TrailerSpec = TRAILER_53FT): LinearFootLine {
  assertUnitShape(u);
  cubicFeet(u.dims); // validates dims
  if (!Number.isSafeInteger(u.quantity) || u.quantity < 1) throw new FreightCalcError("quantity must be a positive integer");
  const { lengthIn, widthIn, heightIn } = u.dims;
  const longSide = Math.max(lengthIn, widthIn);
  const shortSide = Math.min(lengthIn, widthIn);

  // Capped at the quantity: a denormal height would otherwise stack Infinity high (0 positions, 0 ft).
  const stackHeight = u.stackable === true ? Math.min(u.quantity, Math.max(1, Math.floor(trailer.heightIn / heightIn))) : 1;
  const positions = Math.ceil(u.quantity / stackHeight);

  let perRow: number;
  let rowDepth: number;
  if (shortSide * 2 <= trailer.widthIn) {
    const across = Math.floor(trailer.widthIn / shortSide);
    // A denormal width would put Infinity across (0 rows, 0 ft): never more across than positions.
    perRow = Number.isFinite(across) ? across : positions;
    rowDepth = longSide;
  } else if (longSide <= trailer.widthIn) {
    perRow = 1;
    rowDepth = shortSide; // turn the long side across the trailer
  } else {
    perRow = 1;
    rowDepth = longSide;
  }
  const rows = Math.max(1, Math.ceil(positions / perRow));
  const overDimension = longSide > trailer.lengthIn || shortSide > trailer.widthIn || heightIn > trailer.heightIn;
  const linearIn = rows * rowDepth;
  if (!Number.isFinite(linearIn)) throw new FreightCalcError("linear feet are out of range");
  return { positions, stackHeight, perRow, linearIn, overDimension };
}

export interface FreightLineAnalysis {
  index: number;
  cubicFt: number;
  densityPcf: number;
  densityClass: FreightClass;
  declaredClass?: FreightClass;
  /** Class used for rating: declared when present, density class otherwise. */
  effectiveClass: FreightClass;
  classSource: "declared" | "density";
  /** Declared class differs from the density class: likely reclass risk unless NMFC item says otherwise. */
  classMismatch: boolean;
  linear: LinearFootLine;
}

export interface FreightAnalysis {
  lines: FreightLineAnalysis[];
  totalWeightLb: number;
  totalCubicFt: number;
  totalDensityPcf: number;
  /** Class of the whole shipment by aggregate density. */
  shipmentDensityClass: FreightClass;
  linearFt: number;
  /** Linear feet meets or exceeds the carrier linear-foot rule threshold. */
  exceedsLinearFootRule: boolean;
  /** Cube meets or exceeds the volume-quote threshold. */
  exceedsVolumeThreshold: boolean;
  overDimension: boolean;
}

export interface AnalyzeOptions {
  scale?: DensityScaleName;
  trailer?: TrailerSpec;
  /** Most LTL carriers apply cubic-capacity pricing at 10-14 linear ft; default 12. */
  linearFootRuleFt?: number;
  /** Shipments at or above this cube usually need a volume quote; default 750 cu ft. */
  volumeThresholdCuFt?: number;
}

/** Full density/class/cube/linear-foot analysis of a shipment's handling units. */
export function analyzeFreight(units: readonly HandlingUnit[], opts: AnalyzeOptions = {}): FreightAnalysis {
  if (!Array.isArray(units)) throw new FreightCalcError("handling units must be a list");
  if (units.length === 0) throw new FreightCalcError("at least one handling unit is required");
  units.forEach((u, i) => {
    assertUnitShape(u, `handling unit ${i + 1}`);
    // A declared class outside the NMFC set would become the rated/booked class verbatim.
    if (u.freightClass !== undefined && !isNmfcClass(u.freightClass)) {
      throw new FreightCalcError(`handling unit ${i + 1} freight class is not an NMFC class`);
    }
  });
  const o: AnalyzeOptions = opts ?? {};
  assertObject(o, "options");
  const scale = o.scale ?? "nmfc2025";
  resolveScale(scale);
  const trailer = o.trailer ?? TRAILER_53FT;
  assertObject(trailer, "trailer");
  assertPositive(trailer.lengthIn, "trailer lengthIn");
  assertPositive(trailer.widthIn, "trailer widthIn");
  assertPositive(trailer.heightIn, "trailer heightIn");
  const lfRule = o.linearFootRuleFt ?? 12;
  const volThreshold = o.volumeThresholdCuFt ?? 750;

  const lines = units.map((u, index): FreightLineAnalysis => {
    const cube = lineCubicFeet(u);
    const density = densityPcf(u.weightLb, cube);
    const densityClass = classForDensity(density, scale);
    const declared = u.freightClass;
    return {
      index,
      cubicFt: round(cube, 2),
      densityPcf: densityHundredths(density),
      densityClass,
      ...(declared !== undefined ? { declaredClass: declared } : {}),
      effectiveClass: declared ?? densityClass,
      classSource: declared !== undefined ? "declared" : "density",
      classMismatch: declared !== undefined && declared !== densityClass,
      linear: lineLinearFeet(u, trailer),
    };
  });

  const totalWeightLb = units.reduce((s, u) => s + u.weightLb, 0);
  const totalCubicFtRaw = units.reduce((s, u) => s + lineCubicFeet(u), 0);
  const totalDensity = densityPcf(totalWeightLb, totalCubicFtRaw);
  const linearFt = round(lines.reduce((s, l) => s + l.linear.linearIn, 0) / 12, 2);

  return {
    lines,
    totalWeightLb,
    totalCubicFt: round(totalCubicFtRaw, 2),
    totalDensityPcf: densityHundredths(totalDensity),
    shipmentDensityClass: classForDensity(totalDensity, scale),
    linearFt,
    exceedsLinearFootRule: linearFt >= lfRule,
    exceedsVolumeThreshold: totalCubicFtRaw >= volThreshold,
    overDimension: lines.some((l) => l.linear.overDimension),
  };
}

/**
 * Units with `ratedFreightClass` set to the NMFC density class for every class-less unit (the class
 * class-required carriers priced it at); any incoming `ratedFreightClass` is dropped first so only
 * the server's value is stored. Declared classes are untouched. When the density can't be computed
 * (bad dims/weight) units are returned without a rated class.
 */
export function withRatedFreightClasses(units: readonly HandlingUnit[]): HandlingUnit[] {
  if (!Array.isArray(units)) return [];
  const isObj = (u: unknown): u is HandlingUnit => typeof u === "object" && u !== null;
  // Positions are preserved (callers zip by index); a non-object entry is passed through unrated.
  const stripped = units.map((u) => (isObj(u) ? (({ ratedFreightClass: _drop, ...rest }) => rest as HandlingUnit)(u) : u));
  if (!stripped.every(isObj)) return stripped;
  if (!stripped.some((u) => u.freightClass === undefined)) return stripped;
  try {
    const { lines } = analyzeFreight(stripped);
    return stripped.map((u, i) =>
      u.freightClass === undefined ? { ...u, ratedFreightClass: lines[i]!.densityClass } : u,
    );
  } catch {
    return stripped;
  }
}

/** The class a carrier books/prints: the declared class, else the density-rated one. */
export function bookingFreightClass(u: HandlingUnit): FreightClass | undefined {
  if (typeof u !== "object" || u === null) return undefined;
  if (isNmfcClass(u.freightClass)) return u.freightClass;
  // An invalid declared class never falls through to the rated one: the declared value wins or nothing.
  if (u.freightClass !== undefined) return undefined;
  return isNmfcClass(u.ratedFreightClass) ? u.ratedFreightClass : undefined;
}
